import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useRef, useState } from 'react';
import { styleSheet } from './styles';
import { LayoutBoxOverlay } from './LayoutBoxOverlay';
import { LAYOUT_ELEMENT_LABELS, LayoutElement } from './types';

interface EditableQuote {
	text: string;
	color: string;
	onChange: (text: string) => void;
}

interface TestimonialCardCanvasProps {
	canvas?: Api.IGoogleReviewPostCanvas;
	previewHtml: string | null;
	previewError: string | null;
	layout?: Api.IGoogleReviewPostLayout;
	visibleElements: LayoutElement[];
	selectedElement?: LayoutElement;
	onSelectElement?: (element: LayoutElement) => void;
	onLayoutBoxChange?: (element: LayoutElement, box: Api.IGoogleReviewPostLayoutBox) => void;
	editableQuote?: EditableQuote;
	onFrameReady?: (frameDocument: Document) => void;
	fitToContainerHeight?: boolean;
}

export const TestimonialCardCanvas = ({
	canvas,
	previewHtml,
	previewError,
	layout,
	visibleElements,
	selectedElement,
	onSelectElement,
	onLayoutBoxChange,
	editableQuote,
	onFrameReady,
	fitToContainerHeight,
}: TestimonialCardCanvasProps) => {
	const cardContainerRef = useRef<HTMLDivElement>(null);
	const frameRef = useRef<HTMLIFrameElement>(null);
	const [scale, setScale] = useState(0);
	const [isEditingQuote, setIsEditingQuote] = useState(false);
	const [loadedHtml, setLoadedHtml] = useState<string | null>(previewHtml);
	const shownHtmlRef = useRef<string | null>(null);

	const hideCardQuoteWhileEditing = (editing: boolean) => {
		const quote = frameRef.current?.contentDocument?.querySelector('.el.text') as HTMLElement | null;
		if (quote) {
			quote.style.visibility = editing ? 'hidden' : '';
		}
	};

	const reproducesDocumentIgnoringOurDecoration = (live: Document, next: Document): boolean => {
		const quote = live.querySelector('.el.text');
		const decoration = quote?.getAttribute('style') ?? null;
		quote?.removeAttribute('style');
		const reproduced = live.documentElement.outerHTML === next.documentElement.outerHTML;
		if (decoration !== null) {
			quote?.setAttribute('style', decoration);
		}

		return reproduced;
	};

	const patchInPlace = (html: string): boolean => {
		const live = frameRef.current?.contentDocument;
		if (!live?.body) {
			return false;
		}

		const next = new DOMParser().parseFromString(html, 'text/html');
		const liveCard = live.querySelector('.card');
		const nextCard = next.querySelector('.card');
		if (!liveCard || !nextCard) {
			return false;
		}

		liveCard.setAttribute('style', nextCard.getAttribute('style') ?? '');

		for (const selector of ['.el.text', '.el.author']) {
			const liveText = live.querySelector(selector);
			const nextText = next.querySelector(selector);
			if (!liveText || !nextText) {
				return false;
			}
			if (liveText.textContent !== nextText.textContent) {
				liveText.textContent = nextText.textContent;
			}
		}

		return reproducesDocumentIgnoringOurDecoration(live, next);
	};

	useEffect(() => {
		if (previewHtml == null || previewHtml === shownHtmlRef.current) {
			return;
		}

		if (patchInPlace(previewHtml)) {
			shownHtmlRef.current = previewHtml;
			const frameDocument = frameRef.current?.contentDocument;
			if (frameDocument) {
				onFrameReady?.(frameDocument);
			}
			return;
		}

		setLoadedHtml(previewHtml);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [previewHtml]);

	useEffect(() => {
		hideCardQuoteWhileEditing(isEditingQuote);
	}, [isEditingQuote, loadedHtml]);

	const handleFrameLoad = () => {
		const frameDocument = frameRef.current?.contentDocument;
		if (!frameDocument) {
			return;
		}

		shownHtmlRef.current = loadedHtml;
		hideCardQuoteWhileEditing(isEditingQuote);
		onFrameReady?.(frameDocument);
	};

	const canvasWidth = canvas?.width;
	useEffect(() => {
		const node = cardContainerRef.current;
		if (!node || !canvasWidth) {
			return;
		}

		const observer = new ResizeObserver(entries => {
			const width = entries[0]?.contentRect.width ?? 0;
			setScale(width > 0 ? width / canvasWidth : 0);
		});
		observer.observe(node);

		return () => observer.disconnect();
	}, [canvasWidth]);

	return (
		<div
			ref={cardContainerRef}
			className={css(styleSheet.previewContainer)}
			style={
				canvas
					? {
							height: canvas.height * scale,
							...(fitToContainerHeight && { width: `min(100%, ${(100 * canvas.width) / canvas.height}cqh)` }),
						}
					: undefined
			}
		>
			{previewError ? (
				<p className={css(styleSheet.previewMessage)}>{previewError}</p>
			) : loadedHtml && canvas ? (
				<iframe
					ref={frameRef}
					title='Testimonial preview'
					srcDoc={loadedHtml}
					sandbox='allow-same-origin'
					scrolling='no'
					onLoad={handleFrameLoad}
					className={css(styleSheet.previewFrame)}
					style={{ width: canvas.width, height: canvas.height, transform: `scale(${scale})` }}
				/>
			) : (
				<p className={css(styleSheet.previewMessage)}>Loading preview…</p>
			)}

			{loadedHtml &&
				canvas &&
				layout &&
				scale > 0 &&
				visibleElements.map(element => (
					<LayoutBoxOverlay
						key={element}
						box={layout[element]}
						canvas={canvas}
						scale={scale}
						label={LAYOUT_ELEMENT_LABELS[element]}
						isSelected={selectedElement === element}
						onSelect={() => onSelectElement?.(element)}
						onChange={box => onLayoutBoxChange?.(element, box)}
						editableText={element === 'text' ? editableQuote?.text : undefined}
						editableTextColor={element === 'text' ? editableQuote?.color : undefined}
						onEditableTextChange={element === 'text' ? editableQuote?.onChange : undefined}
						onEditingChange={element === 'text' && editableQuote ? setIsEditingQuote : undefined}
					/>
				))}
		</div>
	);
};
